import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Tooltip
} from '@mui/material';
import { calculateCablePrice, formatPrice } from '../services/pricingService';

const standardLengths = [3, 5, 10, 15, 25, 50];

function LengthSelection({ value, onChange, onNext, onBack, config, disableLength }) {
  const [customMode, setCustomMode] = useState(false);
  const [customValue, setCustomValue] = useState(value || '');
  const [error, setError] = useState('');
  const [priceInfo, setPriceInfo] = useState({ totalCost: 0 });
  const [showRoundingMessage, setShowRoundingMessage] = useState(false);

  const validLength =
    value &&
    !isNaN(value) &&
    parseFloat(value) >= 0.5 &&
    parseFloat(value) <= 500;

  const handleStandardClick = (len) => {
    setCustomMode(false);
    setCustomValue('');
    setError('');
    setShowRoundingMessage(false);
    onChange(len);
  };

  const handleCustomClick = () => {
    setCustomMode(true);
    setError('');
    setShowRoundingMessage(false);
  };

  const handleCustomChange = (e) => {
    const val = e.target.value;
    setCustomValue(val); // Store the raw input as user types
    setError(''); // Clear any previous errors
    setShowRoundingMessage(false); // Clear rounding message while typing
    // Don't call onChange yet - wait until user finishes input
  };

  const handleCustomBlur = () => {
    const num = parseFloat(customValue);
    if (isNaN(num) || num < 0.5 || num > 500) {
      setError('Please enter a value between 0.5 and 500 feet');
      setShowRoundingMessage(false);
      onChange(undefined);
    } else {
      setError('');
      // Round to nearest inch in feet (e.g., 5.08 for 5'1", 5.17 for 5'2")
      const roundedLength = Math.round(num * 12) / 12;
      const wasRounded = Math.abs(num - roundedLength) > 0.001; // Check if rounding occurred
      setShowRoundingMessage(wasRounded);
      // Format to exactly 2 decimal places for display
      setCustomValue(roundedLength.toFixed(2)); // Update input to show rounded value with 2 decimals
      onChange(roundedLength);
    }
  };

  const handleCustomKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleCustomBlur();
    }
  };

  // Memoize config to prevent unnecessary recalculations
  const memoizedConfig = useMemo(() => config, [config]);

  // Create a stable safeConfig without using getSafeConfig to avoid object recreation
  const memoizedSafeConfig = useMemo(() => {
    if (!memoizedConfig || !memoizedConfig.type) return null;
    
    // Convert feet to inches for pricing service
    const pricingLength = value ? Math.round(value * 12) : 0;
    
    // Create the safe config structure directly here to avoid object recreation
    return {
      category: memoizedConfig.category || '',
      type: memoizedConfig.type,
      style: memoizedConfig.style || 'end-to-end',
      length: pricingLength, // Use the converted length in inches
      quality: memoizedConfig.quality || '',
      endA: memoizedConfig.connectors?.endA || {},
      endB: memoizedConfig.connectors?.endB || {},
      endC: memoizedConfig.connectors?.endC || {},
      finishes: memoizedConfig.finishing || {},
      hybridConnectors: memoizedConfig.hybridConnectors || {},
      wireGauge: memoizedConfig.wireGauge || {}
    };
  }, [memoizedConfig, value]);

  useEffect(() => {
    let isMounted = true;
    
    async function fetchPrice() {
      if (memoizedSafeConfig && calculateCablePrice) {
        console.log('DEBUG: LengthSelection safeConfig for pricing:', memoizedSafeConfig);
        const result = await calculateCablePrice(memoizedSafeConfig);
        if (isMounted) setPriceInfo(result);
      }
    }
    fetchPrice();
    return () => { isMounted = false; };
  }, [memoizedSafeConfig]);

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 0, mr: 1 }}>
          Select Cable Length
        </Typography>
        <Tooltip 
          title="Cable length measured to center of connector, exact length may vary"
          arrow
          placement="top"
        >
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#ff6600', 
              cursor: 'help',
              fontWeight: 'bold'
            }}
          >
            *
          </Typography>
        </Tooltip>
      </Box>
      <Typography variant="body1" color="text.secondary" paragraph>
        Choose a standard length or enter a custom length (in feet)
      </Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {standardLengths.map((len) => (
          <Grid item key={len}>
            <Button
              variant={value === len ? 'contained' : 'outlined'}
              onClick={() => handleStandardClick(len)}
              sx={{ minWidth: 64 }}
              disabled={!!disableLength}
            >
              {len} ft
            </Button>
          </Grid>
        ))}
        <Grid item>
          <Button
            variant={customMode ? 'contained' : 'outlined'}
            onClick={() => { if (!disableLength) handleCustomClick(); }}
            sx={{ minWidth: 64 }}
            disabled={!!disableLength}
          >
            Custom Length
          </Button>
        </Grid>
      </Grid>
      {customMode && !disableLength && (
        <TextField
          label="Custom Length (ft)"
          type="number"
          value={customValue}
          onChange={handleCustomChange}
          onBlur={handleCustomBlur}
          onKeyPress={handleCustomKeyPress}
          inputProps={{ step: 0.01, min: 0.5, max: 500 }}
          error={!!error}
          helperText={
            error || 
            (showRoundingMessage ? 'Length rounded to nearest inch' : 'Enter a value between 0.5 and 500 feet (will round to nearest inch when finished)')
          }
          sx={{ width: 200 }}
        />
      )}
      {disableLength && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <TextField
            label="Overall Length (ft)"
            type="number"
            value={value}
            disabled
            sx={{ width: 200, mr: 2 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
            <Typography variant="body2" sx={{ color: '#ff6600', fontWeight: 'bold', mr: 0.5 }}>*</Typography>
            <Typography variant="body2" sx={{ color: '#ff6600', fontWeight: 500 }}>
              Pre-populated from custom cable build. Click Next to continue
            </Typography>
          </Box>
        </Box>
      )}
      {validLength && config && config.type && (
        <Box sx={{ mt: 3, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Price:
          </Typography>
          <Typography variant="body2">
            <b>{formatPrice(priceInfo.totalCost)}</b> total
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default LengthSelection; 